import dayjs from 'dayjs';
import { GovTradeOptionI, options, americanStates } from './constants';
import { GovernmentMember, GovernmentTrade, SecurityTrader } from '@benzinga/gov-trades';

export type MembersListItem = GovernmentMember & { ttm_return?: number };

export type MembersChamberFilter = 'all' | 'house' | 'senate';
export type MembersPartyFilter = 'all' | 'D' | 'R' | 'I';
export type MembersSortKey = 'date_of_last_recorded_trade' | 'total_volume_of_trades' | 'number_of_trades';
export type MembersSortOrder = 'asc' | 'desc';

export const MEMBERS_SORT_OPTIONS: { key: MembersSortKey; order: MembersSortOrder; name: string }[] = [
  { key: 'date_of_last_recorded_trade', order: 'desc', name: 'Last Traded: New to Old' },
  { key: 'date_of_last_recorded_trade', order: 'asc', name: 'Last Traded: Old to New' },
  { key: 'total_volume_of_trades', order: 'desc', name: 'Volume: High to Low' },
  { key: 'total_volume_of_trades', order: 'asc', name: 'Volume: Low to High' },
  { key: 'number_of_trades', order: 'desc', name: 'Total Trades: High to Low' },
  { key: 'number_of_trades', order: 'asc', name: 'Total Trades: Low to High' },
];

export const enrichMembersWithReturns = (
  members: GovernmentMember[],
  traders?: { gainers?: SecurityTrader[]; losers?: SecurityTrader[] } | null,
): MembersListItem[] => {
  const returnById = new Map<string, number>();
  for (const trader of [...(traders?.gainers ?? []), ...(traders?.losers ?? [])]) {
    if (trader.member_id && typeof trader.ttm_return === 'number') {
      returnById.set(trader.member_id, trader.ttm_return);
    }
  }
  return members.map(member => {
    const ttm = returnById.get(member.filer_info?.member_id);
    return typeof ttm === 'number' ? { ...member, ttm_return: ttm } : member;
  });
};

export const filterMembersList = (
  members: MembersListItem[],
  chamber: MembersChamberFilter,
  party: MembersPartyFilter,
): MembersListItem[] => {
  return members.filter(member => {
    const info = member.filer_info;
    if (!info) return false;
    if (chamber !== 'all' && (info.chamber ?? '').toLowerCase() !== chamber) return false;
    if (party !== 'all' && info.party !== party) return false;
    return true;
  });
};

export const sortMembersList = (
  members: MembersListItem[],
  key: MembersSortKey,
  order: MembersSortOrder,
): MembersListItem[] => {
  return [...members].sort(sortMembers(key, order));
};

export const memberInitials = (displayName: string | null | undefined): string => {
  if (!displayName?.trim()) return '?';
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const currencyShortForm = (num: number) => {
  const postfixes = [' thousands', ' millions', ' billions'];
  let r = num / 1000;
  let postfixIndex = -1;
  while (r >= 1) {
    postfixIndex++;
    r = r / 1000;
  }
  const formattedNum = num / Math.pow(1000, postfixIndex + 1);
  const positiveFraction = parseInt((formattedNum % 1).toFixed(2));
  return positiveFraction > 0
    ? formattedNum.toFixed(2)
    : formattedNum.toFixed(0) + (postfixIndex > -1 ? postfixes[postfixIndex] : '');
};

export const ChartKeyLabel = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-3">
    <div className="h-4 w-8 rounded" style={{ background: color }} />
    <div className="text-sm font-normal text-bzgray-600">{label}</div>
  </div>
);

export const getPoliticalPartyName = (party: string) => {
  const partyNames = [
    { name: 'Democrat', party: 'D' },
    { name: 'Republican', party: 'R' },
    { name: 'Independent', party: 'I' },
  ];
  return partyNames.find(p => p.party === party)?.name ?? '-';
};

export const getFullStateName = (state: string) => {
  return americanStates.find(s => s.state === state)?.name.toLowerCase() ?? '-';
};

const FILER_STATUS_FORMER_MEMBER = 'Former Member' as const;

const FILER_ACTIVE_MEMBER_STATUSES = new Set<string>(['Member', 'Current Member']);

export type CurrentMemberLabel = 'yes' | 'no' | '—';

export const currentMemberLabelFromStatus = (status: string | undefined | null): CurrentMemberLabel => {
  if (status == null) return '—';
  const s = status.trim();
  if (s === '') return '—';
  if (s === FILER_STATUS_FORMER_MEMBER) return 'no';
  if (FILER_ACTIVE_MEMBER_STATUSES.has(s)) return 'yes';
  return '—';
};

export const sortMembers = (key: string, order: string) => {
  return (a: GovernmentMember, b: GovernmentMember) => {
    let valA = a.stats?.[key as keyof NonNullable<GovernmentMember['stats']>] as string | number | undefined;
    let valB = b.stats?.[key as keyof NonNullable<GovernmentMember['stats']>] as string | number | undefined;

    if (key === 'date_of_last_recorded_trade') {
      valA = dayjs(valA).valueOf();
      valB = dayjs(valB).valueOf();
    } else {
      valA = typeof valA === 'number' ? valA : 0;
      valB = typeof valB === 'number' ? valB : 0;
    }

    if (order === 'asc') {
      return (valA as number) - (valB as number);
    }
    return (valB as number) - (valA as number);
  };
};

export const processSortingMembers = (data: GovernmentMember[]): GovTradeOptionI[] => {
  if (!data || data.length === 0) return options;

  const sortedMembers = options.map(option => {
    const members = [...data].sort(sortMembers(option.key, option.order));
    option.members = members;
    return option;
  });
  return sortedMembers;
};

const TRADING_ACTIVITY_WINDOW_MS = 365 * 24 * 60 * 60 * 1000;

export const findTopVolumeLeader = (members: GovernmentMember[]): GovernmentMember | null => {
  if (!members?.length) return null;
  const cutoff = Date.now() - TRADING_ACTIVITY_WINDOW_MS;
  let best: GovernmentMember | null = null;
  let bestVolume = -1;

  for (const m of members) {
    const vol = m.stats?.total_volume_of_trades;
    const lastStr = m.stats?.date_of_last_recorded_trade;
    if (typeof vol !== 'number' || vol <= 0 || !lastStr) continue;
    const lastMs = dayjs(lastStr).valueOf();
    if (!Number.isFinite(lastMs) || lastMs < cutoff) continue;
    if (vol > bestVolume) {
      bestVolume = vol;
      best = m;
    }
  }

  return best;
};

export type NetBuySellDatum = { ticker: string; company: string; buys: number; sells: number; total: number };

export const buildNetBuySellByTicker = (recentTrades: GovernmentTrade[], topN = 6): NetBuySellDatum[] => {
  const byTicker = new Map<string, NetBuySellDatum>();

  for (const trade of recentTrades) {
    const ticker = trade.security?.ticker;
    if (!ticker) continue;

    let datum = byTicker.get(ticker);
    if (!datum) {
      datum = { ticker, company: '', buys: 0, sells: 0, total: 0 };
      byTicker.set(ticker, datum);
    }
    if (!datum.company && trade.security?.name) {
      datum.company = trade.security.name;
    }

    const side = (trade.transaction_type ?? '').trim().charAt(0).toUpperCase();
    if (side === 'P') {
      datum.buys += 1;
    } else if (side === 'S') {
      datum.sells += 1;
    }
  }

  const result: NetBuySellDatum[] = [];
  for (const datum of byTicker.values()) {
    const total = datum.buys + datum.sells;
    if (total > 0) {
      result.push({ ...datum, company: datum.company || datum.ticker, total });
    }
  }

  result.sort((a, b) => (b.total !== a.total ? b.total - a.total : a.ticker.localeCompare(b.ticker)));

  return result.slice(0, topN);
};
